import React, { useState, useRef, useEffect } from 'react';
import { navigate, Link } from "gatsby"


// Helper function to extract pathname from URL (for Gatsby navigation)
const getRelativePath = (url) => {
    if (!url) return url;

    // If it's already a relative path, return as is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return url;
    }

    try {
        const urlObj = new URL(url);
        return urlObj.pathname + urlObj.search + urlObj.hash;
    } catch (e) {
        // If URL parsing fails, try to extract pathname manually
        const urlMatch = url.match(/^https?:\/\/[^\/]+(.*)$/);
        if (urlMatch) {
            return urlMatch[1];
        }
        return url;
    }
};

const Results = ({ results, photo, close }) => {
    const [expanded, setExpanded] = useState(false);
    const [paddingTop, setPaddingTop] = useState(0);
    const cardRef = useRef(null);
    const productsRef = useRef(null);
    const products = results?.products || [];
    const categories = results?.categories?.slice(0, 2);
    const shapes = results?.shapes || [];
    const [clickedProduct, setClickedProduct] = useState(null);
    const [clickedCategory, setClickedCategory] = useState(null);

    const filteredResults = (() => {
        const maxTotal = 6;
        const idealProducts = 5;

        const availableProducts = products.length;
        const availableShapes = shapes.length;

        const productsCount = availableShapes > 0
            ? Math.min(availableProducts, idealProducts)
            : Math.min(availableProducts, maxTotal);

        const shapesNeeded = Math.min(
            maxTotal - productsCount,
            availableShapes
        );

        return [
            ...products?.slice(0, productsCount),
            ...shapes?.slice(0, shapesNeeded)
        ];
    })();

    useEffect(() => {
        if (productsRef.current && filteredResults?.length > 0) {
            let productsHeight = filteredResults?.length <= 3 ? productsRef.current.offsetHeight : productsRef.current.offsetHeight / 2;
            let windowHeight = window.innerHeight;
            setPaddingTop(windowHeight - productsHeight - 32);
        }
        else {
            // calculate so that it shows the whole card when there are no results
            let windowHeight = window.innerHeight;
            let cardHeight = cardRef.current.offsetHeight;
            setPaddingTop(windowHeight - cardHeight);
        }
    }, [filteredResults]);



    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const cardElement = cardRef.current;
        const productsElement = productsRef.current;

        if (!cardElement) return;

        if (filteredResults?.length > 0 && productsElement) {
            productsElement.scrollIntoView({
                block: 'end',
                behavior: 'smooth'
            });
        } else {
            cardElement.scrollIntoView({
                behavior: 'smooth',
                block: 'end'
            });
        }
    }, [results, filteredResults.length, paddingTop]);

    const getRandomLabel = () => {
        const labels = [
            "We couldn't find a match this time.|Try snapping another photo or browse our categories.",
            "Nothing came up for that one.|You can retake the photo or check out categories instead.",
            "Nothing found this round.|You can take another photo or browse categories for ideas.",
            "We couldn't recognize that item.|Try a new angle or browse categories to find something similar.",
            "Looks like we couldn't match that one.|Try taking a different photo or browse our categories.",
        ]

        return labels[Math.floor(Math.random() * labels.length)];
    }


    const handleProductClick = (product) => {
        setClickedProduct(product?.title || null);
        const url = getRelativePath(product?.url);
        navigate(url, {
            state: {
                visualSearch: {
                    conversationId: results?.conversationId
                }
            }
        });

        setTimeout(() => {
            setClickedProduct(null);
        }, 1000);
    }


    const handleLiveChatClick = () => {
        window.LiveChatWidget.call("maximize");
    }




    const isCurrentRelativeUrl = (url) => {
        if (typeof window === 'undefined' || !url) {
            return false;
        }

        try {
            const targetUrl = new URL(url, window.location.origin);
            const targetRelative = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
            const currentRelative = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            return targetRelative === currentRelative;
        } catch (e) {
            return false;
        }
    };

    const handleCategoryClick = (category, event) => {
        setClickedCategory(category?.title || null);

        const categoryUrl = getRelativePath(category?.url);
        if (isCurrentRelativeUrl(categoryUrl)) {
            event?.preventDefault();
            close?.();
        }

        setTimeout(() => {
            setClickedCategory(null);
        }, 1000);
    }

    return (
        <div className='camera__results-container' style={{ paddingTop: paddingTop }}>
            <div
                ref={cardRef}
                className={`camera__results`}

            >
                <div ref={productsRef} className='camera__results-products'>
                    {filteredResults?.map((product, index) => (
                        <div onClick={() => handleProductClick(product)} key={index} className={`camera__results-product ${clickedProduct === product?.title ? 'camera__results-product__clicked' : ''}`}>
                            <div className='camera__results-product-image-wrapper'>
                                <img src={product.image} alt={product.title} />
                            </div>
                            <div className='camera__results-product-title'>
                                {product?.title}
                            </div>
                        </div>
                    ))}
                </div>
                {filteredResults?.length === 0 && (
                    <div>
                        <p className='camera__results-no-results-text'>
                            {getRandomLabel().split('|').map((line, index, array) => (
                                <React.Fragment key={index}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                </React.Fragment>
                            ))}
                        </p>
                    </div>
                )}
                <div className="camera__results-categories">
                    {categories.map((category, index) => (
                        <Link to={getRelativePath(category.url)} onClick={(event) => handleCategoryClick(category, event)} key={category.id + index} className={`camera__results-category ${clickedCategory === category?.title ? 'camera__results-category__clicked' : ''}`}>
                            {category.title}
                        </Link>
                    ))}
                </div>
                <div onClick={() => handleLiveChatClick()} className="camera__results-contact">
                    Live Support
                    <div className='camera__results-contact-arrow'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
                            <path d="M0.749559 6.00001C0.749559 6.14919 0.808823 6.29227 0.914311 6.39776C1.0198 6.50324 1.16288 6.56251 1.31206 6.56251H12.3293L8.22659 10.6645C8.17433 10.7168 8.13287 10.7788 8.10459 10.8471C8.0763 10.9154 8.06175 10.9886 8.06175 11.0625C8.06175 11.1364 8.0763 11.2096 8.10459 11.2779C8.13287 11.3462 8.17433 11.4082 8.22659 11.4605C8.27885 11.5127 8.3409 11.5542 8.40918 11.5825C8.47746 11.6108 8.55065 11.6253 8.62456 11.6253C8.69847 11.6253 8.77165 11.6108 8.83994 11.5825C8.90822 11.5542 8.97026 11.5127 9.02253 11.4605L14.085 6.39798C14.1373 6.34574 14.1788 6.2837 14.2071 6.21541C14.2354 6.14713 14.25 6.07393 14.25 6.00001C14.25 5.92609 14.2354 5.85289 14.2071 5.7846C14.1788 5.71632 14.1373 5.65428 14.085 5.60204L9.02253 0.539539C8.91698 0.433991 8.77383 0.374695 8.62456 0.374695C8.47529 0.374695 8.33214 0.433991 8.22659 0.539539C8.12104 0.645087 8.06175 0.78824 8.06175 0.937508C8.06175 1.08677 8.12104 1.22993 8.22659 1.33548L12.3293 5.43751H1.31206C1.16288 5.43751 1.0198 5.49677 0.914311 5.60226C0.808823 5.70775 0.749559 5.85082 0.749559 6.00001Z" fill="#2F2F2F" />
                        </svg>

                    </div>
                </div>

            </div>

        </div>
    );
};

export default Results;